import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import { api } from "../lib/api";
import { useAuth } from "./AuthContext";
import { toast } from "sonner";

const ContentContext = createContext(null);

export function ContentProvider({ children }) {
  const { user } = useAuth();
  const [overrides, setOverrides] = useState({});
  const [ready, setReady] = useState(false);
  const [editMode, setEditMode] = useState(false);
  const pendingRef = useRef({});
  const saveTimerRef = useRef(null);

  useEffect(() => {
    api.get("/content").then(({ data }) => setOverrides(data || {})).catch(() => {}).finally(() => setReady(true));
  }, []);

  // Auto-turn edit mode off when user logs out
  useEffect(() => {
    if (!user || user.role !== "admin") setEditMode(false);
  }, [user]);

  const flushSave = useCallback(async () => {
    const updates = { ...pendingRef.current };
    if (Object.keys(updates).length === 0) return;
    pendingRef.current = {};
    try {
      await api.patch("/admin/content", { updates });
      toast.success("Saved", { duration: 1500 });
    } catch (e) {
      toast.error("Save failed — please retry");
    }
  }, []);

  const update = useCallback((key, value) => {
    setOverrides((prev) => ({ ...prev, [key]: value }));
    pendingRef.current[key] = value;
    if (saveTimerRef.current) clearTimeout(saveTimerRef.current);
    saveTimerRef.current = setTimeout(flushSave, 900);
  }, [flushSave]);

  const t = useCallback((key, fallback) => {
    const v = overrides[key];
    return v !== undefined && v !== null && v !== "" ? v : fallback;
  }, [overrides]);

  const isAdmin = !!(user && user.role === "admin");

  return (
    <ContentContext.Provider value={{ t, update, overrides, ready, isAdmin, editMode, setEditMode }}>
      {children}
    </ContentContext.Provider>
  );
}

export const useContent = () => useContext(ContentContext);
